import * as XLSX from 'xlsx';
import type { DeviceColumn } from '../components/aditools-device-table.ts';
import type { DeviceTableDeviceData } from '@ebiz/aditools-databook';
import { downloadFile } from './download.ts';

////////////////////////////////////////////////////////////////////////////////////
// export-to-excel - Class containing functions for export to excel (xlsx format) //
////////////////////////////////////////////////////////////////////////////////////

export default {
  ////////////////////////////////////////////////////////////////////////////////
  // Functions                                                                  //
  ////////////////////////////////////////////////////////////////////////////////

  getVersion(): string {
    return XLSX.version;
  },
  exportTable(
    columns: DeviceColumn<DeviceTableDeviceData>[],
    rows: DeviceTableDeviceData[],
    locale: string,
    dataSourceName: string | undefined,
  ): void {
    const workBook = XLSX.utils.book_new();
    const now = new Date();
    const createDateStamp =
      ('0' + (now.getMonth() + 1)).slice(-2) +
      '/' +
      ('0' + now.getDate()).slice(-2) +
      '/' +
      now.getFullYear() +
      ' ' +
      ('0' + (((now.getHours() + 11) % 12) + 1)).slice(-2) +
      ':' +
      ('0' + now.getMinutes()).slice(-2) +
      ':' +
      ('0' + now.getSeconds()).slice(-2) +
      ' ' +
      (now.getHours() >= 12 ? 'PM' : 'AM');
    workBook.Props = {
      Title: 'Table Export',
      Subject: 'Exporting Table Data',
      CreatedDate: now,
    };
    workBook.SheetNames.push('Cover');
    workBook.SheetNames.push('Web Display');
    workBook.SheetNames.push('Raw Data Display');

    // Build Cover
    const coverData: Array<Array<string>> = [];
    coverData.push(['Date Created:', createDateStamp]);
    coverData.push(['Product Type:', dataSourceName ? dataSourceName : '']);

    // Build Display and Raw Worksheets
    const displaySheetData: Array<Array<string>> = [];
    const rawSheetData: Array<Array<string | number>> = [];
    const headerNameRowLine: Array<string> = [];
    const headerContextRowLine: Array<string> = [];
    const headerUnitRowLine: Array<string> = [];

    columns.forEach((column) => {
      const name = column.name + (column.minMaxTyp ? ` (${column.minMaxTyp})` : '');
      const context = column.fieldContext ? column.fieldContext : '';
      const unit = column.unit && column.unit !== 'n/a' ? column.unit : '';
      headerNameRowLine.push(name);
      headerContextRowLine.push(context);
      headerUnitRowLine.push(unit);
    });

    const hasContextRow = headerContextRowLine.filter((item) => item !== '').length > 0;
    displaySheetData.push(headerNameRowLine);
    rawSheetData.push(headerNameRowLine);
    if (hasContextRow) {
      displaySheetData.push(headerContextRowLine);
      rawSheetData.push(headerContextRowLine);
    }
    displaySheetData.push(headerUnitRowLine);
    rawSheetData.push(headerUnitRowLine);
    rows.forEach((row) => {
      const rawRow: Array<string | number> = [];
      const displayRow: Array<string> = [];
      columns.forEach((column) => {
        if (column.id !== 'partNumber') {
          const displayId = row['display_' + column.id] ? 'display_' + column.id : (column.id as string);
          const rawId = row['value_' + column.id] ? 'value_' + column.id : (column.id as string);
          const displayField: string = row[displayId]
            ? Array.isArray(row[displayId])
              ? ((row[displayId] as Array<unknown>).join(', ') as string)
              : (row[displayId] as string)
            : '';
          const rawField: string | number = row[rawId]
            ? Array.isArray(row[rawId])
              ? ((row[rawId] as Array<unknown>).join(', ') as string)
              : typeof row[rawId] === 'number'
                ? (row[rawId] as number)
                : (row[rawId] as string)
            : '';
          displayRow.push(displayField);
          rawRow.push(rawField);
        } else {
          displayRow.push(row['partNumber'] as string);
          rawRow.push(row['partNumber'] as string);
        }
      });
      displaySheetData.push(displayRow);
      rawSheetData.push(rawRow);
    });
    workBook.Sheets['Cover'] = XLSX.utils.aoa_to_sheet(coverData);
    workBook.Sheets['Web Display'] = XLSX.utils.aoa_to_sheet(displaySheetData);
    workBook.Sheets['Raw Data Display'] = XLSX.utils.aoa_to_sheet(rawSheetData);

    // Add links to the part numbers
    const wRange = XLSX.utils.decode_range(workBook.Sheets['Web Display']['!ref'] as string);
    const rowCount = wRange.e.r - wRange.s.r + 1;
    const dataStartRow = hasContextRow ? 4 : 3;
    for (let i = dataStartRow; i <= rowCount; i++) {
      const cell = 'A' + i.toString();
      workBook.Sheets['Web Display'][cell].l = {
        Target: 'https://www.analog.com/' + locale + '/' + workBook.Sheets['Web Display'][cell].v,
      };
      workBook.Sheets['Raw Data Display'][cell].l = {
        Target: 'https://www.analog.com/' + locale + '/' + workBook.Sheets['Raw Data Display'][cell].v,
      };
    }

    // Format Column Widths for Cover
    let columnMaxLength: Array<number> = [];
    coverData.forEach((arr) => {
      arr.forEach((value, key) => {
        columnMaxLength[key] = Math.max(columnMaxLength[key] ?? 0, value.length);
      });
    });
    workBook.Sheets['Cover']['!cols'] = columnMaxLength.map((width) => {
      return { width: width + 2 };
    });

    // Format Column Widths for Web Display
    columnMaxLength = [];
    displaySheetData.forEach((arr) => {
      arr.forEach((value, key) => {
        columnMaxLength[key] = Math.max(columnMaxLength[key] ?? 0, value.length);
      });
    });
    workBook.Sheets['Web Display']['!cols'] = columnMaxLength.map((width) => {
      return { width: width + 2 };
    });

    // Format Column Widths for Raw Display
    columnMaxLength = [];
    rawSheetData.forEach((arr) => {
      arr.forEach((value, key) => {
        let len = 0;
        switch (typeof value) {
          case 'number':
            len = 10;
            break;
          case 'string':
            len = value.length;
            break;
          default:
            len = 10;
            break;
        }
        columnMaxLength[key] = Math.max(columnMaxLength[key] ?? 0, len);
      });
    });
    workBook.Sheets['Raw Data Display']['!cols'] = columnMaxLength.map((width) => {
      return { width: width + 2 };
    });
    downloadFile(
      'application/octet-stream',
      'Export.xlsx',
      new Blob([XLSX.write(workBook, { type: 'array', bookType: 'xlsx' })]),
    );
  },
};
